import { immediateStructuralParent } from '../bubbleEmphasis';
import { ancestryChipsForNode, ancestryChipsFromVolume } from '../../../lib/bubble/ancestryChips';
import type { PackNode } from '../../../lib/bubble/packNodeTypes';
import {
  isInstanceDrillLevel,
  isStatusDrillLevel,
} from '../../../lib/sidebar/sidebarPalettes';
import { bubbleNodeTooltipHtml } from '../../../lib/tooltipHtml';
import type { HierarchyNode } from '../../../types/exploration';
import { resolveFullVolume } from './bubbleClickDispatch';
import type { BubbleRenderOptions } from './bubbleRenderOptions';

const tooltipLeafTypes = new Set(['volume', 'status', 'reason', 'overflow']);

function findAncestor(
  d: PackNode,
  predicate: (node: PackNode) => boolean
): PackNode | null {
  for (const ancestor of d.ancestors?.() ?? [d]) {
    if (predicate(ancestor)) return ancestor;
  }
  return null;
}

function isGroupListingDepth(options: BubbleRenderOptions): boolean {
  const { location } = options;
  return !isInstanceDrillLevel(location) && !isStatusDrillLevel(location);
}

/** Resolve which pack node should drive the hover tooltip (group card vs leaf detail). */
export function resolveTooltipPackNode(
  d: PackNode,
  options: BubbleRenderOptions
): PackNode {
  const sidebarHl = options.getSidebarHighlight?.();
  if (sidebarHl?.kind === 'group') {
    const match = findAncestor(
      d,
      (ancestor) =>
        ancestor.data.name === sidebarHl.name &&
        (!sidebarHl.type || ancestor.data.type === sidebarHl.type)
    );
    if (match) return match;
  }

  if (!tooltipLeafTypes.has(d.data.type ?? '')) {
    return d;
  }

  if (!isGroupListingDepth(options)) {
    return d;
  }

  const leafType = d.data.type ?? '';
  if (leafType === 'status' || leafType === 'reason' || leafType === 'overflow') {
    return d;
  }

  return immediateStructuralParent(d) ?? d;
}

function tooltipVariant(node: PackNode['data']): 'group' | 'status' | 'volume' {
  if (node.type === 'volume') return 'volume';
  if (node.type === 'status' || node.type === 'reason') return 'status';
  // Overflow bubbles render via groupBreakdownCardHtml (same shape as a group bubble),
  // so they need the wider default tip-card width, not the narrower 'sc vt' width.
  return 'group';
}

export function showBubbleTooltip(
  e: MouseEvent,
  d: PackNode,
  options: BubbleRenderOptions
) {
  const resolved = resolveTooltipPackNode(d, options);
  const nodeData = resolved.data;
  const variant = tooltipVariant(nodeData);
  const drillAncestryOpts = {
    location: options.location,
    secondary: options.secondary,
    groupByAccount: options.groupByAccount,
  };
  const volumeAncestryOpts = {
    secondary: options.secondary,
    tagKey: options.tagKey,
    groupByAccount: options.groupByAccount,
  };
  const volume =
    nodeData.type === 'volume' && nodeData.volume
      ? resolveFullVolume(options.volumes, nodeData.volume.ebsId)
      : undefined;
  const ancestry =
    volume
      ? ancestryChipsFromVolume(volume, volumeAncestryOpts)
      : nodeData.type === 'status' || nodeData.type === 'reason'
        ? ancestryChipsForNode(resolved, drillAncestryOpts)
        : undefined;
  options.showTooltip(
    bubbleNodeTooltipHtml(nodeData as HierarchyNode, {
      accountTotalSize: options.accountTotalSize,
      view: options.view,
      drillPath: options.drillPath,
      ancestry,
    }),
    e.clientX,
    e.clientY,
    variant
  );
}
